import React, { useState } from 'react';
import { 
  Search, Filter, ChevronDown, ChevronUp, ChevronRight,
  MoreVertical, Eye, Edit, Mail, Phone, Calendar,
  Users, Gift, Award, Clock, UserCheck, MessageCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Status Badge Component
export function StatusBadge({ status, compact = false }) {
  const statusConfig = {
    nuevo_lead: { label: 'Lead', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    registrado: { label: 'Registrado', color: 'bg-blue-400/20 text-blue-300 border-blue-400/30' },
    contactado: { label: 'Contactado', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    prueba_agendada: { label: 'Visita', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    instalado: { label: 'Instalado', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    activo: { label: 'Activo', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
    validado: { label: 'Validado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    recompensa_entregada: { label: 'Completado', color: 'bg-white/20 text-white border-white/30' }
  };

  const config = statusConfig[status] || { label: status, color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 lg:px-2.5 lg:py-1 rounded-md text-[10px] lg:text-xs font-medium border ${config.color}`}>
      {compact ? config.label.substring(0, 3) : config.label}
    </span>
  );
}

// Payment Progress Bar - Responsive with "Registrar mensualidad" logic
export function PaymentProgress({ payments, max = 3, onAddPayment, loading, compact = false, buttonLabel = null }) {
  const isValidated = payments >= max;
  const monthLabels = ['1ª', '2ª', '3ª'];
  
  return (
    <div className={`flex items-center gap-2 lg:gap-3 ${compact ? 'flex-col sm:flex-row' : ''}`}>
      <div className={`flex gap-1 ${compact ? 'w-full sm:flex-1' : 'flex-1'}`}>
        {Array.from({ length: max }).map((_, index) => (
          <div
            key={index}
            className={`flex-1 h-1.5 lg:h-2 rounded-full transition-all duration-300 relative group ${
              index < payments 
                ? 'bg-white shadow-[0_0_6px_rgba(255,255,255,0.5)]' 
                : 'bg-white/10'
            }`}
            title={`${monthLabels[index]} mensualidad ${index < payments ? '✓' : ''}`}
          />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] lg:text-xs font-mono ${isValidated ? 'text-emerald-400 font-semibold' : 'text-zinc-400'}`}>
          {payments}/{max}
        </span>
        {onAddPayment && payments < max && (
          <Button
            size="sm"
            onClick={onAddPayment}
            disabled={loading}
            className="h-6 lg:h-7 px-2 text-[10px] lg:text-xs bg-white text-black hover:bg-zinc-200 rounded whitespace-nowrap"
            data-testid="registrar-mensualidad-btn"
          >
            {loading ? '...' : buttonLabel || 'Registrar'}
          </Button>
        )}
        {isValidated && (
          <span className="text-emerald-400 text-[10px] lg:text-xs font-medium flex items-center gap-1">
            <Award className="w-3 h-3" />
            <span className="hidden sm:inline">VALIDADO</span>
          </span>
        )}
      </div>
    </div>
  );
}

// Mobile Client Card
function ClientCard({ client, onView, onAddReferral }) {
  return (
    <div 
      className="p-4 rounded-lg bg-[#0A0A0A] border border-white/10 space-y-3"
      onClick={() => onView?.(client)}
      data-testid={`client-card-${client.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-white">
              {client.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{client.name}</p>
            <p className="text-xs text-zinc-500 truncate">{client.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-zinc-400 hover:text-white h-8 w-8 p-0"
          onClick={(e) => { e.stopPropagation(); onView?.(client); }}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="flex items-center justify-between text-xs">
        <span className="font-mono text-zinc-400">{client.referral_code}</span>
        <div className="flex items-center gap-3">
          <span className="text-zinc-500">{client.total_referrals} ref</span>
          <span className="text-emerald-400">{client.validated_referrals} val</span>
          {client.available_rewards > 0 && (
            <span className="flex items-center gap-1 text-amber-400">
              <Gift className="w-3 h-3" />
              {client.available_rewards}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Desktop Client Row Component
function ClientRow({ client, onView, onAddReferral }) {
  return (
    <tr 
      className="border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer"
      onClick={() => onView?.(client)}
      data-testid={`client-row-${client.id}`}
    >
      <td className="py-3 lg:py-4 px-3 lg:px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
            <span className="text-xs lg:text-sm font-medium text-white">
              {client.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-xs lg:text-sm font-medium text-white truncate">{client.name}</p>
            <p className="text-[10px] lg:text-xs text-zinc-500 truncate">{client.email}</p>
          </div>
        </div>
      </td>
      <td className="py-3 lg:py-4 px-3 lg:px-4 hidden md:table-cell">
        <span className="font-mono text-xs lg:text-sm text-zinc-300">{client.referral_code}</span>
      </td>
      <td className="py-3 lg:py-4 px-3 lg:px-4 text-center">
        <span className="text-xs lg:text-sm text-white">{client.total_referrals}</span>
      </td>
      <td className="py-3 lg:py-4 px-3 lg:px-4 text-center hidden sm:table-cell">
        <span className="text-xs lg:text-sm text-emerald-400">{client.validated_referrals}</span>
      </td>
      <td className="py-3 lg:py-4 px-3 lg:px-4 text-center hidden sm:table-cell">
        {client.available_rewards > 0 ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/10 text-white text-xs">
            <Gift className="w-3 h-3" />
            {client.available_rewards}
          </span>
        ) : (
          <span className="text-zinc-500">-</span>
        )}
      </td>
      <td className="py-3 lg:py-4 px-3 lg:px-4">
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onView?.(client)}
            className="h-7 lg:h-8 w-7 lg:w-8 p-0 text-zinc-400 hover:text-white hover:bg-white/10"
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

// Mobile Referral Card
function ReferralCard({ referral, onView, onAddPayment, loadingPayment }) {
  const handleWhatsApp = (e) => {
    e.stopPropagation();
    if (referral.phone) {
      const message = encodeURIComponent(`Hola ${referral.name}, te contactamos desde Manklar.`);
      window.open(`https://wa.me/${referral.phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
    }
  };

  const handleCall = (e) => {
    e.stopPropagation();
    if (referral.phone) {
      window.open(`tel:${referral.phone.replace(/\s/g, '')}`);
    }
  };

  return (
    <div 
      className="p-4 rounded-lg bg-[#0A0A0A] border border-white/10 space-y-3"
      onClick={() => onView?.(referral)}
      data-testid={`referral-card-${referral.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-white">
              {referral.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{referral.name}</p>
            <p className="text-xs text-zinc-500">por {referral.referrer_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={referral.status} compact />
          {referral.status === 'validado' && <span className="text-base">🎁</span>}
        </div>
      </div>
      
      {/* Quick Actions for mobile */}
      {referral.phone && (
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <button 
            onClick={handleCall}
            className="flex-1 h-8 flex items-center justify-center gap-1 rounded bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors text-xs"
          >
            <Phone className="w-3 h-3" />
            Llamar
          </button>
          <button 
            onClick={handleWhatsApp}
            className="flex-1 h-8 flex items-center justify-center gap-1 rounded bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#25D366] transition-colors text-xs"
          >
            <MessageCircle className="w-3 h-3" />
            WhatsApp
          </button>
        </div>
      )}
      
      <div onClick={(e) => e.stopPropagation()}>
        <PaymentProgress 
          payments={referral.payments_made}
          onAddPayment={referral.payments_made < 3 ? () => onAddPayment?.(referral.id) : null}
          loading={loadingPayment === referral.id}
          compact
          buttonLabel="Registrar mensualidad"
        />
      </div>
    </div>
  );
}

// Desktop Referral Row Component
function ReferralRow({ referral, onView, onAddPayment, loadingPayment }) {
  const handleWhatsApp = (e) => {
    e.stopPropagation();
    if (referral.phone) {
      const message = encodeURIComponent(`Hola ${referral.name}, te contactamos desde Manklar.`);
      window.open(`https://wa.me/${referral.phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
    }
  };

  const handleEmail = (e) => {
    e.stopPropagation();
    if (referral.email) {
      window.open(`mailto:${referral.email}?subject=Manklar - Información de tu servicio`);
    }
  };

  return (
    <tr 
      className="border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer"
      onClick={() => onView?.(referral)}
      data-testid={`referral-row-${referral.id}`}
    >
      <td className="py-3 lg:py-4 px-3 lg:px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
            <span className="text-xs lg:text-sm font-medium text-white">
              {referral.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-xs lg:text-sm font-medium text-white truncate">{referral.name}</p>
            <p className="text-[10px] lg:text-xs text-zinc-500 truncate">{referral.email || 'Sin email'}</p>
          </div>
        </div>
      </td>
      <td className="py-3 lg:py-4 px-3 lg:px-4 hidden md:table-cell">
        <div className="min-w-0">
          <p className="text-xs lg:text-sm text-zinc-300 truncate">{referral.referrer_name}</p>
          <p className="text-[10px] lg:text-xs text-zinc-500 font-mono truncate">{referral.referrer_code}</p>
        </div>
      </td>
      <td className="py-3 lg:py-4 px-3 lg:px-4">
        <StatusBadge status={referral.status} />
      </td>
      <td className="py-3 lg:py-4 px-3 lg:px-4 w-32 lg:w-56 hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
        <PaymentProgress 
          payments={referral.payments_made} 
          onAddPayment={referral.payments_made < 3 ? () => onAddPayment?.(referral.id) : null}
          loading={loadingPayment === referral.id}
          buttonLabel="Registrar"
        />
      </td>
      <td className="py-3 lg:py-4 px-3 lg:px-4 text-center hidden sm:table-cell">
        {referral.status === 'validado' && (
          <span className="text-lg" title="Recompensa desbloqueada">🎁</span>
        )}
      </td>
      <td className="py-3 lg:py-4 px-3 lg:px-4">
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {referral.phone && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleWhatsApp}
              className="h-7 lg:h-8 w-7 lg:w-8 p-0 text-[#25D366] hover:text-[#25D366] hover:bg-[#25D366]/10"
              title="WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
          )}
          {referral.email && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEmail}
              className="h-7 lg:h-8 w-7 lg:w-8 p-0 text-zinc-400 hover:text-white hover:bg-white/10"
              title="Enviar email"
            >
              <Mail className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onView?.(referral)}
            className="h-7 lg:h-8 w-7 lg:w-8 p-0 text-zinc-400 hover:text-white hover:bg-white/10"
            title="Ver perfil"
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

// Sort Icon Helper - moved outside component to avoid React reconciliation issues
function SortIndicator({ field, sortField, sortDirection }) {
  if (sortField !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
  return sortDirection === 'asc' 
    ? <ChevronUp className="w-3 h-3" /> 
    : <ChevronDown className="w-3 h-3" />;
}

// Clients Table Component - Responsive (cards on mobile, table on desktop)
export function ClientsTable({ 
  clients, 
  onViewClient, 
  onAddReferral,
  searchTerm,
  onSearchChange
}) {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm?.toLowerCase() || '') ||
    client.email.toLowerCase().includes(searchTerm?.toLowerCase() || '') ||
    client.referral_code.toLowerCase().includes(searchTerm?.toLowerCase() || '')
  );

  const sortedClients = [...filteredClients].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    const modifier = sortDirection === 'asc' ? 1 : -1;
    if (typeof aVal === 'string') {
      return aVal.localeCompare(bVal) * modifier;
    }
    return (aVal - bVal) * modifier;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Empty state
  if (sortedClients.length === 0) {
    return (
      <div className="py-12 text-center rounded-lg border border-white/10 bg-[#0A0A0A]">
        <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No se encontraron clientes</p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile: Card view */}
      <div className="space-y-3 md:hidden">
        {sortedClients.map(client => (
          <ClientCard 
            key={client.id} 
            client={client}
            onView={onViewClient}
            onAddReferral={onAddReferral}
          />
        ))}
      </div>

      {/* Desktop: Table view */}
      <div className="hidden md:block rounded-lg border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="clients-table">
            <thead>
              <tr className="bg-[#0A0A0A] border-b border-white/10">
                <th 
                  className="py-3 lg:py-4 px-3 lg:px-4 text-left text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Cliente <SortIndicator field="name" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th className="py-3 lg:py-4 px-3 lg:px-4 text-left text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider hidden md:table-cell">
                  Código
                </th>
                <th 
                  className="py-3 lg:py-4 px-3 lg:px-4 text-center text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => handleSort('total_referrals')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Ref <SortIndicator field="total_referrals" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th className="py-3 lg:py-4 px-3 lg:px-4 text-center text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider hidden sm:table-cell">
                  Val
                </th>
                <th className="py-3 lg:py-4 px-3 lg:px-4 text-center text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider hidden sm:table-cell">
                  Recomp
                </th>
                <th className="py-3 lg:py-4 px-3 lg:px-4 text-right text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider w-16">
                  
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#0A0A0A]/50">
              {sortedClients.map(client => (
                <ClientRow 
                  key={client.id} 
                  client={client}
                  onView={onViewClient}
                  onAddReferral={onAddReferral}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// Referrals Table Component - Responsive
export function ReferralsTable({ 
  referrals, 
  onViewReferral, 
  onAddPayment,
  loadingPayment,
  searchTerm 
}) {
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const filteredReferrals = referrals.filter(ref =>
    ref.name.toLowerCase().includes(searchTerm?.toLowerCase() || '') ||
    (ref.email && ref.email.toLowerCase().includes(searchTerm?.toLowerCase() || '')) ||
    ref.referrer_name.toLowerCase().includes(searchTerm?.toLowerCase() || '')
  );

  const sortedReferrals = [...filteredReferrals].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    const modifier = sortDirection === 'asc' ? 1 : -1;
    if (typeof aVal === 'string') {
      return aVal.localeCompare(bVal) * modifier;
    }
    return (aVal - bVal) * modifier;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Empty state
  if (sortedReferrals.length === 0) {
    return (
      <div className="py-12 text-center rounded-lg border border-white/10 bg-[#0A0A0A]">
        <UserCheck className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No se encontraron referidos</p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile: Card view */}
      <div className="space-y-3 md:hidden">
        {sortedReferrals.map(referral => (
          <ReferralCard 
            key={referral.id} 
            referral={referral}
            onView={onViewReferral}
            onAddPayment={onAddPayment}
            loadingPayment={loadingPayment}
          />
        ))}
      </div>

      {/* Desktop: Table view */}
      <div className="hidden md:block rounded-lg border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="referrals-table">
            <thead>
              <tr className="bg-[#0A0A0A] border-b border-white/10">
                <th 
                  className="py-3 lg:py-4 px-3 lg:px-4 text-left text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Referido <SortIndicator field="name" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th className="py-3 lg:py-4 px-3 lg:px-4 text-left text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider hidden md:table-cell">
                  Referidor
                </th>
                <th 
                  className="py-3 lg:py-4 px-3 lg:px-4 text-left text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Estado <SortIndicator field="status" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th className="py-3 lg:py-4 px-3 lg:px-4 text-left text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider hidden sm:table-cell">
                  Pagos
                </th>
                <th className="py-3 lg:py-4 px-3 lg:px-4 text-center text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider hidden sm:table-cell">
                  🎁
                </th>
                <th className="py-3 lg:py-4 px-3 lg:px-4 text-right text-[10px] lg:text-xs font-medium text-zinc-500 uppercase tracking-wider w-16">
                  
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#0A0A0A]/50">
              {sortedReferrals.map(referral => (
                <ReferralRow 
                  key={referral.id} 
                  referral={referral}
                  onView={onViewReferral}
                  onAddPayment={onAddPayment}
                  loadingPayment={loadingPayment}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// Top Referrers List - Responsive
export function TopReferrersList({ referrers, onViewClient }) {
  if (!referrers || referrers.length === 0) {
    return (
      <div className="p-4 lg:p-6 rounded-lg bg-[#0A0A0A] border border-white/10">
        <h3 className="text-[10px] lg:text-sm uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-4">
          Top Referidores
        </h3>
        <p className="text-center text-zinc-500 py-8 text-sm">Sin datos disponibles</p>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 rounded-lg bg-[#0A0A0A] border border-white/10">
      <h3 className="text-[10px] lg:text-sm uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-4">
        Top Referidores
      </h3>
      <div className="space-y-2 lg:space-y-3">
        {referrers.slice(0, 5).map((referrer, index) => (
          <div 
            key={referrer.id}
            className="flex items-center gap-3 lg:gap-4 p-2 lg:p-3 rounded-lg hover:bg-white/5 transition-colors cursor-pointer"
            onClick={() => onViewClient?.(referrer)}
          >
            <span className="w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] lg:text-xs font-bold text-white flex-shrink-0">
              {index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs lg:text-sm font-medium text-white truncate">{referrer.name}</p>
              <p className="text-[10px] lg:text-xs text-zinc-500 font-mono truncate">{referrer.referral_code}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-base lg:text-lg font-semibold text-white">{referrer.total_referrals}</p>
              <p className="text-[10px] lg:text-xs text-emerald-400">{referrer.validated_referrals} val</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ClientsTable;
